/**
 * Блок полей редактирования свойств компонента на карточке сущности.
 * Зависит от типа атрибута.
 * У разных типов есть свой набор возможных компонентов.
 */
import { EditOutlined, EyeOutlined, FormOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Form, Input, Select } from "antd";
import DisabledContext from "antd/es/config-provider/DisabledContext";
import * as React from "react";
import { AttrEditorCompName, editorsByType } from "src/common/attrEdit";
import { classNames } from "src/common/classNames";
import { ZTooltipExtFields } from "src/components/ExtendedTooltip";
import { getTooltipMode } from "src/components/ExtendedTooltip/getTooltipMode";
import { InputNumberStd } from "src/components/InputNumberStd";
import { AttrTypeName } from "src/types/AttrType";
import { ZOption } from "src/types/ZOption";
import { edAttrField } from "../../../../EdAttribute";
import { ComponentPreview } from "../ComponentPreview";
import { dateCmpOptions } from "../dateCmpOptions";
import { validatorRegexpInput } from "../RegexpInput";
import { RegexpInput } from "../RegexpInput/RegexpInput";
import { RelativeCmpEdit } from "../RelativeCmpEdit";
import { TooltipExtEdit } from "../TooltipExtEdit/TooltipExtEdit";
import { defEditorComponentProps } from "./defEditorComponentProps";
import styles from "./EditorInfo2.module.less";
import {
  edItemField,
  editorField,
  ParamsEditorInfo2,
} from "./ParamsEditorInfo2";

const relativeCmpTypes = new Set<string>([
  AttrTypeName.date,
  AttrTypeName.dateTime,
  AttrTypeName.time,
  AttrTypeName.int,
  AttrTypeName.number,
]);

interface PropsEditorInfo {
  params: ParamsEditorInfo2;
}

export const EditorInfo2: React.FC<PropsEditorInfo> = ({ params }) => {
  const { attrTypesDict, curAttrId, disabled, objectId } = params;

  const [openPreview, setOpenPreview] = React.useState(false);
  const [tooltipExtOpen, setTooltipExtOpen] = React.useState(false);
  const form = Form.useFormInstance();
  const attrType = Form.useWatch(edAttrField.valueType);
  const tooltipExt = Form.useWatch(edItemField("tooltipExt")) ?? {};
  const tooltipText = Form.useWatch(edItemField("tooltip"));

  const typeName = attrTypesDict[attrType] ?? String(attrType);
  const curEditor: AttrEditorCompName | undefined = Form.useWatch(editorField);
  const componentNames: ZOption[] = React.useMemo(() => {
    const editors: string[] = editorsByType[typeName as AttrTypeName] ?? [];
    if (curEditor && !editors.includes(curEditor)) {
      form.resetFields(editorField);
    }
    return editors.map((value) => ({ value, label: value }));
  }, [attrType]);

  const isTodayRule =
    typeName === AttrTypeName.date || typeName === AttrTypeName.dateTime;
  const isRelativeCmpRule = relativeCmpTypes.has(typeName);

  const isEditorRequired =
    typeName === AttrTypeName.dictSingle || typeName === AttrTypeName.dictMulti;

  return (
    <DisabledContext.Provider value={disabled}>
      <div className={classNames([styles.box, styles.editor])}>
        <div className={styles.boxTitle}>
          <FormOutlined /> На карточке экземпляра (форма)
        </div>
        <div className={styles.fields}>
          <Form.Item
            name={editorField}
            label="Компонент"
            rules={[{ required: isEditorRequired }]}
          >
            <Select allowClear options={componentNames} />
          </Form.Item>
          <Form.Item
            name={edItemField("required")}
            valuePropName="checked"
            noStyle
          >
            <Checkbox>Является обязательным для заполнения</Checkbox>
          </Form.Item>
          {curEditor &&
            defEditorComponentProps(curEditor, {
              ...params,
              attrTypeName: typeName,
              form,
            })}
          <Divider className={styles.divider}>Дополнительные параметры</Divider>
          <div className={styles.threeCols}>
            <Form.Item
              label="Колонки"
              name={edItemField("colspan")}
              tooltip="Сколько колонок займёт компонент на карточке экземпляра"
            >
              <InputNumberStd min={1} max={3} />
            </Form.Item>
            <Form.Item
              label="Строки"
              name={edItemField("rowspan")}
              tooltip="Сколько ячеек по вертикали займёт компонент на карточке экземпляра"
            >
              <InputNumberStd min={1} />
            </Form.Item>
            <Form.Item
              name={edItemField("newLine")}
              label="С новой строки"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
          </div>
          {typeName === AttrTypeName.string && (
            <Form.Item
              label="Шаблон для проверки значения"
              name={edItemField("pattern")}
              rules={[validatorRegexpInput]}
            >
              <RegexpInput />
            </Form.Item>
          )}

          <Form.Item name={edItemField("tooltipExt")} hidden>
            <ExtTooltipItem
              tooltipText={tooltipText}
              open={tooltipExtOpen}
              close={() => setTooltipExtOpen(false)}
            />
          </Form.Item>
          <Form.Item
            name={edItemField("tooltip")}
            label="Всплывающая подсказка"
          >
            <Input
              allowClear
              addonAfter={
                <Button
                  size="small"
                  type="text"
                  icon={
                    getTooltipMode({ tooltipExt }) === "ext" ? (
                      <FormOutlined />
                    ) : (
                      <EditOutlined />
                    )
                  }
                  onClick={() => setTooltipExtOpen(true)}
                />
              }
            />
          </Form.Item>
          {isTodayRule && (
            <Form.Item
              name={edItemField("todayRule")}
              label="Ограничение относительно сегодня"
            >
              <Select options={dateCmpOptions()} allowClear />
            </Form.Item>
          )}
          {isRelativeCmpRule && (
            <Form.Item
              name={edItemField("relativeCmpRule")}
              label="Сравнение с другим полем"
            >
              <RelativeCmpEdit
                typeName={typeName}
                objectId={objectId}
                curAttrId={curAttrId}
                typesMap={attrTypesDict}
              />
            </Form.Item>
          )}

          <div>
            <Button
              className={styles.viewButton}
              icon={<EyeOutlined />}
              onClick={() => setOpenPreview(true)}
            >
              Просмотр
            </Button>
            <ComponentPreview
              objectId={String(objectId)}
              open={openPreview}
              close={() => setOpenPreview(false)}
              typesMap={attrTypesDict}
              attrForm={form}
            />
          </div>
        </div>
      </div>
    </DisabledContext.Provider>
  );
};

interface PropsExtTooltipItem {
  open: boolean;
  close(): void;
  value?: ZTooltipExtFields;
  onChange?(newValue: ZTooltipExtFields | undefined): void;
  tooltipText: string | undefined;
}

const ExtTooltipItem: React.FC<PropsExtTooltipItem> = (props) => {
  const { open, close, value, onChange, tooltipText } = props;
  const form = Form.useFormInstance();
  return (
    <TooltipExtEdit
      open={open}
      onCancel={close}
      getInitialData={() => ({
        text: form.getFieldValue(edItemField("tooltip")),
        ...(value ?? {}),
      })}
      onOk={(fields) => {
        onChange?.(fields);
        close();
        form.setFieldValue(edItemField("tooltip"), fields.text);
      }}
      tooltipText={tooltipText}
    />
  );
};
